/**
 * Value labels for a filter condition read back from SQL — the text the filter
 * chip shows next to a lookup key (`42 — Acme Corp`) or a lookup-enum key
 * (`Active ·3`), for surfaces that show the condition without the chip (the
 * Visual Query Editor's WHERE / HAVING pills). Pure: the labels come from
 * MetaInfo's cached pairs or the caller's already-fetched row.
 */

import type { DatabaseMetaInfo, EnumLookupTarget } from '../../../../types/electron';
import type { FilterCondition, TableExplorerColumn } from '../../core/types/workspaceTypes';
import { findLooseRelationshipsForColumn } from './looseFkResolver';
import { resolveLookupEnumTarget } from './lookupEnumTarget';

/**
 * The FK target `column` reads lookup-enum pairs through — the enum picker's
 * rule (`useEnumPickerSlot`): a real FK constraint, else exactly one
 * unconditional column-level loose FK. Needed to recognise an IMPLICIT lookup
 * enum (no declaration; the target's table is flagged as a reference table).
 */
export function lookupEnumTargetFor(
  column: TableExplorerColumn,
  schema: string,
  table: string,
  metaInfo: DatabaseMetaInfo | null | undefined,
): EnumLookupTarget | undefined {
  const resolution = resolveLookupEnumTarget({
    dataType: column.dataType,
    realFk:
      column.referencedTable && column.referencedColumn && !column.isLoose
        ? {
            ...(column.referencedSchema != null ? { referencedSchema: column.referencedSchema } : {}),
            referencedTable: column.referencedTable,
            referencedColumn: column.referencedColumn,
          }
        : undefined,
    looseRels: findLooseRelationshipsForColumn(metaInfo?.looseRelationships, schema, table, column.name),
  });
  return resolution.target ?? undefined;
}

/**
 * The label text for an `enum` condition's key(s): each key's cached label, or
 * the key itself when it has none (`Active, 5`). `null` when no key has a
 * label — the value then shows as written.
 */
export function enumConditionLabelText(
  value: FilterCondition['value'],
  labels: ReadonlyMap<string, string | null> | undefined,
): string | null {
  if (!labels || labels.size === 0 || value === null || value === undefined) return null;
  const keys = (Array.isArray(value) ? value : [value]).map((k) => (k instanceof Date ? k.toISOString() : String(k)));
  const texts = keys.map((k) => {
    const label = labels.get(k);
    return label === null || label === undefined || label === '' ? null : label;
  });
  if (texts.every((t) => t === null)) return null;
  return texts.map((t, i) => t ?? keys[i]).join(', ');
}

/**
 * True for a top-level `lookup` condition, or a JSON `property_lookup` pick
 * (`operator: 'json'`, `jsonSubOperator: 'property_lookup'`) — the JSON
 * FK-picker's scalar shape, reopened as `'json'` when re-emitting it as the
 * top-level `lookup` writes different SQL (see `upgradeReopenedCondition`'s
 * `::jsonb`-cast comment). Both read the referenced row through the same
 * loose-FK declaration; only the path used to find it differs.
 */
function isLookupCondition(condition: FilterCondition): boolean {
  return condition.operator === 'lookup' || (condition.operator === 'json' && condition.jsonSubOperator === 'property_lookup');
}

/** A `lookup` (top-level or JSON `property_lookup`) condition's single key, or null when it has none to label. */
export function lookupConditionKey(condition: FilterCondition): string | number | bigint | boolean | Date | null {
  const { value } = condition;
  if (!isLookupCondition(condition) || value === null || value === undefined || value === '' || Array.isArray(value)) {
    return null;
  }
  return value;
}

/**
 * The path `useFkLookupLabel` resolves the active loose-FK declaration by —
 * the condition's own `sourcePath` for the top-level `lookup` operator, or
 * its JSONPath (`condition.path`) for a JSON `property_lookup` pick, mirroring
 * `FkLookupValueButton`'s `activeLooseRel` resolution in `JsonFilterFields`
 * (`resolveActiveLooseRel(rels, pathValue)`).
 */
export function lookupConditionSourcePath(condition: FilterCondition): string | undefined {
  if (condition.operator === 'json' && condition.jsonSubOperator === 'property_lookup') {
    return condition.path;
  }
  return condition.sourcePath;
}
